import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '@/config/firebase';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    // Check if user profile exists, create if not
    const userDocRef = doc(db, 'users', result.user.uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      // Update last login
      await updateDoc(userDocRef, {
        lastLogin: serverTimestamp(),
        isOnline: true
      });
    } else {
      // Create user profile - default to hq_admin for first user
      const isAdmin = email === 'admin@digitalscreens.com';
      await setDoc(userDocRef, {
        id: result.user.uid,
        uid: result.user.uid,
        email: result.user.email,
        name: isAdmin ? 'HQ Administrator' : 'Store Manager',
        role: isAdmin ? 'hq_admin' : 'store_manager',
        assignedLocations: isAdmin ? [] : ['loc-london-1', 'loc-london-2'],
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        isOnline: true
      });
    }
    return result;
  };

  const register = async (email, password, userData) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Create user profile in Firestore
    await setDoc(doc(db, 'users', result.user.uid), {
      uid: result.user.uid,
      email,
      ...userData,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      isOnline: true
    });
    return result;
  };

  const logout = async () => {
    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          isOnline: false,
          lastLogout: serverTimestamp()
        });
      } catch (e) {
        console.log('Could not update user status on logout');
      }
    }
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    logout,
    register,
    loading,
    isHQ: userProfile?.role === 'hq_admin',
    isManager: userProfile?.role === 'store_manager'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
